import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { isValidChecksumAddress, isValidAddress } from "ethereumjs-util";
import { toast } from "react-toastify";
import ScholarOverview, { ScholarProps } from "./ScholarOverview";
import { appVersion } from "../../App";

const Wrapper = styled.div`
  width: 100%;
  padding: 12px 0;
`;

const InnerWrapper = styled.div`
  max-width: 640px;
  margin: 0 auto;
  @media (max-width: 640px) {
    margin: 0 20px;
  }
`;

const Input = styled.input`
  padding: 6px;
  @media (max-width: 540px) {
    width: 100%;
  }
`;

const Button = styled.button`
  padding: 6px 8px;
  background: gold;
  border: none;
  border-radius: 4px;
`;

const Form = styled.form`
  & > * {
    margin-right: 8px;
    @media (max-width: 540px) {
      margin-right: 0;
      margin-bottom: 8px;
    }
  }
  margin-bottom: 16px;
`;

const Table = styled.table`
  width: 100%;
  td,
  th {
    border: 1px solid #ddd;
    padding: 2px;
  }
  tr:nth-child(even) {
    background-color: #f2f2f2;
  }
  tr:hover {
    background-color: #ddd;
  }
  th {
    padding-top: 12px;
    padding-bottom: 12px;
    text-align: left;
    background-color: #4caf50;
    color: white;
  }
`;

const Select = styled.select`
  padding: 6px;
  @media (max-width: 540px) {
    width: 100%;
  }
`;

const DevNote = styled.p`
  background: #aaa;
  font-size: 0.98rem;
  padding: 5px;
`;

export type ViewMode = "Card" | "Table";

const ScholarsFC: React.FC = () => {
  const [ethAddress, setEthAddress] = useState<string>("");
  const [nickname, setNickname] = useState<string>("");
  const [scholarList, setScholarList] = useState<ScholarProps[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("Table");
  const [slpQuota, setSlpQuota] = useState<string>("75");

  useEffect(() => {
    const scholarsJSON = localStorage.getItem("scholars");
    if (scholarsJSON) {
      setScholarList(JSON.parse(scholarsJSON));
    }
    const prefViewMode = localStorage.getItem("pref:viewMode");
    if (prefViewMode) {
      setViewMode(prefViewMode as ViewMode);
    }
    const prefSlpQuota = localStorage.getItem("pref:slpQuota");
    if (prefSlpQuota) {
      setSlpQuota(prefSlpQuota);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("pref:viewMode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem("scholars", JSON.stringify(scholarList));
  }, [scholarList]);

  useEffect(() => {
    if (!Number.isNaN(Number(slpQuota)))
      localStorage.setItem("pref:slpQuota", slpQuota);
  }, [slpQuota]);

  return (
    <Wrapper>
      <InnerWrapper>
        <div>{appVersion}</div>
        <hr />
        <Form onSubmit={(e) => e.preventDefault()}>
          <Input
            type="text"
            name="eth-address"
            placeholder="Ronin address"
            value={ethAddress}
            onChange={(e) => {
              setEthAddress(e.target.value);
            }}
          />
          <Input
            type="text"
            name="nickname"
            placeholder="Nickname"
            value={nickname}
            onChange={(e) => {
              setNickname(e.target.value);
            }}
          />
          <Button
            onClick={() => {
              let cleanAddress = ethAddress.toLowerCase().trim();

              if (cleanAddress.includes("ronin:")) {
                cleanAddress = cleanAddress.replace("ronin:", "0x");
              }

              const exist = scholarList.some(
                (d) => d.ethAddress.toLowerCase() === cleanAddress
              );
              if (exist) {
                toast.error("Eherium Address already exist!");
              } else if (!cleanAddress) {
                toast.error("Please Input Ethereum Address!");
              } else if (!nickname) {
                toast.error("Please add a nickname for easier recognition");
              } else if (
                isValidAddress(cleanAddress) ||
                isValidChecksumAddress(cleanAddress)
              ) {
                setScholarList((scholarList) => [
                  ...scholarList,
                  {
                    nickname: nickname,
                    ethAddress: cleanAddress,
                  },
                ]);
                setEthAddress("");
                setNickname("");
              } else {
                toast.error("Invalid etherium address!");
              }
            }}
          >
            Add Scholar
          </Button>
        </Form>
        <label>
          View Mode&nbsp;
          <Select
            value={viewMode}
            onChange={(e) => {
              setViewMode(e.target.value as ViewMode);
            }}
          >
            <option value="Card">Card</option>
            <option value="Table">Table</option>
          </Select>
        </label>
        <br />
        <br />
        <label>
          Daily Quota&nbsp;
          <Input
            type="number"
            min={0}
            max={1000}
            value={slpQuota}
            maxLength={4}
            onChange={(event) => {
              setSlpQuota(event.target.value);
            }}
          />
        </label>
        <br />
        <br />
        <DevNote>
          Update: Please use Ronin address instead of the ETH address
        </DevNote>
        <hr />
        {scholarList.length <= 0 && <p>Add Scholar to continue</p>}
        {/* <Table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Link</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {scholarList.map((d, index) => (
              <ScholarOverview
                key={d.ethAddress}
                {...d}
                index={index}
                onRemove={(ethAddress) =>
                  setScholarList((scholarList) =>
                    scholarList.filter((d) => d.ethAddress !== ethAddress)
                  )
                }
                viewMode="Table"
              slpQuota={Number(slpQuota)}
              />
            ))}
          </tbody>
        </Table> */}
        {viewMode === "Card" ? (
          scholarList.map((d, index) => (
            <ScholarOverview
              {...d}
              index={index}
              onRemove={(ethAddress) =>
                setScholarList((scholarList) =>
                  scholarList.filter((d) => d.ethAddress !== ethAddress)
                )
              }
              viewMode="Card"
              slpQuota={Number(slpQuota)}
            />
          ))
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Last Claimed</th>
                <th>Average</th>
                <th>Claimable on</th>
                <th>Total</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {scholarList.map((d, index) => (
                <ScholarOverview
                  key={d.ethAddress}
                  {...d}
                  index={index}
                  onRemove={(ethAddress) =>
                    setScholarList((scholarList) =>
                      scholarList.filter((d) => d.ethAddress !== ethAddress)
                    )
                  }
                  viewMode="Table"
                  slpQuota={Number(slpQuota)}
                />
              ))}
            </tbody>
          </Table>
        )}
      </InnerWrapper>
    </Wrapper>
  );
};

const Scholars = React.memo(ScholarsFC);

export default Scholars;
